/**
 * Admin Application - Root Component
 * 
 * Main app routing with sidebar navigation and admin pages.
 * Handles authentication and page routing.
 * 
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemText, Tooltip, IconButton, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Collapse } from '@mui/material';
import { Dashboard, AssignmentInd, AttachMoney, Campaign, Feedback, Logout, People, Collections, AdminPanelSettings, BarChart, ChevronLeft, ChevronRight, Category, ViewCarousel, Groups, EmojiPeople, RateReview, Star, Insights, FormatQuote, Email, Settings, ExpandLess, ExpandMore, Gavel, Description, Announcement } from '@mui/icons-material';

// Components
import AdminHeader from './components/AdminHeader';

// Pages
import AdminLoginPage from './pages/AdminLoginPage';
import MyAccountPage from './pages/MyAccountPage';
import DashboardPage from './pages/DashboardPage';
import CampaignsPage from './pages/CampaignsPage';
import CampaignAddPage from './pages/CampaignAddPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import CampaignEditPage from './pages/CampaignEditPage';
import CampaignCategoriesPage from './pages/CampaignCategoriesPage';
import CampaignSubmissionsPage from './pages/CampaignSubmissionsPage';
import CampaignSubmissionReviewPage from './pages/CampaignSubmissionReviewPage';
import CampaignRatingsPage from './pages/CampaignRatingsPage';
import UsersPage from './pages/UsersPage';
import AdminGalleryPage from './pages/AdminGalleryPage';
import GalleryAddPage from './pages/GalleryAddPage';
import GalleryEditPage from './pages/GalleryEditPage';
import GalleryDetailPage from './pages/GalleryDetailPage';
import DonationsPage from './pages/DonationsPage';
import DonationDetailPage from './pages/DonationDetailPage';
import VolunteersPage from './pages/VolunteersPage';
import ContactsPage from './pages/ContactsPage';
import SubAdminPage from './pages/SubAdminPage';
import SubAdminFormPage from './pages/SubAdminFormPage';
import ReportsPage from './pages/ReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import HomeSlidersPage from './pages/HomeSlidersPage';
import HomePopupsPage from './pages/HomePopupsPage';
import SiteStatsPage from './pages/SiteStatsPage';
import TestimonialsPage from './pages/TestimonialsPage';
import NewslettersPage from './pages/NewslettersPage';
import LegalPagesPage from './pages/LegalPagesPage';
import FinancialDocumentsPage from './pages/FinancialDocumentsPage';
import FoundersPage from './pages/FoundersPage';
import TeamPage from './pages/TeamPage';
import TeamMemberEditPage from './pages/TeamMemberEditPage';

import logger from './logger';

const drawerWidth = 232;
const drawerWidthCollapsed = 72;
const SIDEBAR_OPEN_KEY = 'adminSidebarOpen';

/**
 * Protected Route Component
 * Checks if user is authenticated before allowing access
 */
function ProtectedRoute({ isAuthenticated, children }) {
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
}

function PermissionRoute({ isAuthenticated, canAccess, children }) {
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  if (!canAccess) return <Navigate to="/admin/dashboard" replace />;
  return children;
}

/**
 * Admin Application Component
 */
function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_OPEN_KEY);
    return stored !== null ? stored === 'true' : true;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('adminToken');
  });
  const [authUser, setAuthUser] = useState(() => {
    try {
      const raw = localStorage.getItem('adminUser');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  const toggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_OPEN_KEY, String(next));
      return next;
    });
  };

  // Check if user is logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    let parsedUser = null;
    try {
      const raw = localStorage.getItem('adminUser');
      parsedUser = raw ? JSON.parse(raw) : null;
    } catch (_) {
      parsedUser = null;
    }
    setIsAuthenticated(!!token);
    setAuthUser(parsedUser);
    logger.info('Admin app initialized', { isAuthenticated: !!token });

    // Listen for storage changes (when login happens in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'adminToken') {
        setIsAuthenticated(!!localStorage.getItem('adminToken'));
      }
      if (e.key === 'adminUser') {
        try {
          const raw = localStorage.getItem('adminUser');
          setAuthUser(raw ? JSON.parse(raw) : null);
        } catch (_) {
          setAuthUser(null);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom token-updated event (same tab login)
    const handleTokenUpdate = () => {
      const token = localStorage.getItem('adminToken');
      setIsAuthenticated(!!token);
      try {
        const raw = localStorage.getItem('adminUser');
        setAuthUser(raw ? JSON.parse(raw) : null);
      } catch (_) {
        setAuthUser(null);
      }
      logger.info('Token updated, isAuthenticated:', !!token);
    };
    
    window.addEventListener('tokenUpdated', handleTokenUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenUpdated', handleTokenUpdate);
    };
  }, []);

  const menuItems = [
    { text: 'Dashboard', path: '/admin/dashboard', icon: Dashboard },
    {
      text: 'Settings',
      icon: Settings,
      children: [
        { text: 'Home Slider', path: '/admin/home-sliders', icon: ViewCarousel },
        { text: 'Home Popup', path: '/admin/home-popups', icon: Announcement },
        { text: 'Site Stats', path: '/admin/site-stats', icon: Insights },
        { text: 'Testimonials', path: '/admin/testimonials', icon: FormatQuote, hidden: true },
        { text: 'Newsletters', path: '/admin/newsletters', icon: Email },
        { text: 'Founders', path: '/admin/founders', icon: EmojiPeople },
        { text: 'Team Members', path: '/admin/team-members', icon: Groups },
        { text: 'Contact Inquiries', path: '/admin/contacts', icon: Feedback },
        { text: 'Legal Pages', path: '/admin/legal-pages', icon: Gavel },
        { text: 'Financial Documents', path: '/admin/financial-documents', icon: Description },
      ],
    },
    {
      text: 'Campaigns',
      icon: Campaign,
      children: [
        { text: 'Campaigns', path: '/admin/campaigns', icon: Campaign },
        { text: 'Campaign Submissions', path: '/admin/campaign-submissions', icon: RateReview },
        { text: 'Campaign Ratings', path: '/admin/campaign-ratings', icon: Star },
      ],
    },
    { text: 'Categories', path: '/admin/campaign-categories', icon: Category },
    { text: 'Donations', path: '/admin/donations', icon: AttachMoney },
    {
      text: 'Users',
      icon: People,
      children: [
        { text: 'Users', path: '/admin/users', icon: People },
        { text: 'Volunteers', path: '/admin/volunteers', icon: AssignmentInd },
        { text: 'Sub Admins', path: '/admin/sub-admins', icon: AdminPanelSettings },
      ],
    },
    { text: 'Gallery', path: '/admin/gallery', icon: Collections },
    { text: 'Reports', path: '/admin/reports', icon: BarChart },
  ];

  const role = String(authUser?.role || '').toLowerCase().replace(/\s+/g, '_');
  let permissions = {};
  if (authUser?.permissions && typeof authUser.permissions === 'object') {
    permissions = authUser.permissions;
  } else if (typeof authUser?.permissions === 'string') {
    try {
      permissions = JSON.parse(authUser.permissions);
    } catch (_) {
      permissions = {};
    }
  }
  const hasAnyPermissionKey = permissions && Object.keys(permissions).length > 0;
  // Super admin gets full access.
  // Backward compatibility: primary admin accounts may come as role=admin with is_admin=true and no module map.
  const isSuperAdmin =
    role === 'super_admin' ||
    role === 'superadmin' ||
    (authUser?.is_admin === true || authUser?.is_admin === 1) && !hasAnyPermissionKey;
  // Strict policy: only super_admin has full access.
  // Every other role is permission-limited, including role=admin.
  const isPermissionLimitedUser = !isSuperAdmin;

  const hasModuleAccess = (moduleKey) => {
    if (isSuperAdmin) return true;
    if (!moduleKey) return true;
    const v = permissions[moduleKey];
    return v === true || v === 1 || v === '1' || v === 'true';
  };

  const isPathActive = (path) => {
    if (!path) return false;
    if (location.pathname === path) return true;
    return path !== '/admin/dashboard' && location.pathname.startsWith(`${path}/`);
  };

  const canSeePath = (path) => {
    if (isSuperAdmin) return true;
    if (path === '/admin/dashboard') return true;
    if (path === '/admin/sub-admins') return false;
    if (path === '/admin/home-sliders') {
      return hasModuleAccess('home_slider') || hasModuleAccess('gallery');
    }
    if (path === '/admin/home-popups') {
      return hasModuleAccess('home_popup') || hasModuleAccess('home_slider');
    }
    if (path === '/admin/site-stats') {
      return hasModuleAccess('site_stats') || hasModuleAccess('home_slider');
    }
    if (path === '/admin/testimonials') {
      return hasModuleAccess('testimonials') || hasModuleAccess('home_slider');
    }
    if (path === '/admin/newsletters') {
      return hasModuleAccess('newsletters') || hasModuleAccess('contacts');
    }
    if (path === '/admin/legal-pages') {
      return hasModuleAccess('legal_pages') || hasModuleAccess('home_slider');
    }
    if (path === '/admin/financial-documents') {
      return hasModuleAccess('financial_documents') || hasModuleAccess('legal_pages');
    }
    if (path === '/admin/founders' || path.startsWith('/admin/team/')) {
      return hasModuleAccess('founders') || hasModuleAccess('team');
    }
    if (path === '/admin/team-members') {
      return hasModuleAccess('team_members') || hasModuleAccess('team');
    }
    if (path === '/admin/campaign-categories') {
      return hasModuleAccess('campaign_categories') || hasModuleAccess('campaigns');
    }
    if (path.startsWith('/admin/campaign-submissions')) {
      return hasModuleAccess('campaign_submissions') || hasModuleAccess('campaigns');
    }
    if (path.startsWith('/admin/campaign-ratings')) {
      return hasModuleAccess('ratings') || hasModuleAccess('campaigns');
    }
    if (path.startsWith('/admin/campaigns')) {
      return hasModuleAccess('campaigns');
    }
    if (path.startsWith('/admin/donations')) return hasModuleAccess('donations');
    if (path.startsWith('/admin/volunteers')) return hasModuleAccess('volunteers');
    if (path.startsWith('/admin/users')) return hasModuleAccess('users');
    if (path.startsWith('/admin/gallery')) return hasModuleAccess('gallery');
    if (path.startsWith('/admin/reports')) return hasModuleAccess('reports');
    if (path.startsWith('/admin/contacts')) return hasModuleAccess('contacts');
    return true;
  };

  const visibleMenuItems = menuItems
    .map((item) => {
      if (item.children) {
        const children = item.children.filter((child) => !child.hidden && canSeePath(child.path));
        if (!children.length) return null;
        return { ...item, children };
      }
      return canSeePath(item.path) ? item : null;
    })
    .filter(Boolean);

  const isGroupOpen = (item) => {
    if (typeof openGroups[item.text] === 'boolean') return openGroups[item.text];
    return Boolean(item.children?.some((child) => isPathActive(child.path)));
  };

  const toggleGroup = (item) => {
    if (!sidebarOpen) {
      setSidebarOpen(true);
      localStorage.setItem(SIDEBAR_OPEN_KEY, 'true');
    }
    setOpenGroups((prev) => ({
      ...prev,
      [item.text]: !(typeof prev[item.text] === 'boolean' ? prev[item.text] : item.children?.some((child) => isPathActive(child.path))),
    }));
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setIsAuthenticated(false);
    setAuthUser(null);
    setOpenLogoutDialog(false);
    logger.info('User logged out');
    navigate('/admin/login');
  };

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleCancelLogout = () => {
    setOpenLogoutDialog(false);
  };

  // Show login page
  if (location.pathname === '/admin/login' || !isAuthenticated) {
    return <AdminLoginPage />;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const drawer = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <List
        sx={{
          p: sidebarOpen ? 2 : 1,
          pt: 3,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {visibleMenuItems.map((item) => {
          if (item.children) {
            const Icon = item.icon;
            const childActive = item.children.some((child) => isPathActive(child.path));
            const open = isGroupOpen(item);
            const parentButton = (
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => toggleGroup(item)}
                  sx={{
                    borderRadius: '8px',
                    justifyContent: sidebarOpen ? 'initial' : 'center',
                    minHeight: 48,
                    px: sidebarOpen ? 2 : 1.5,
                    backgroundColor: childActive ? 'rgba(44, 95, 45, 0.08)' : 'transparent',
                    borderLeft: childActive ? '4px solid #2c5f2d' : 'none',
                    '&:hover': { backgroundColor: 'rgba(44, 95, 45, 0.1)' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Icon sx={{ mr: sidebarOpen ? 2 : 0, color: childActive ? '#2c5f2d' : '#666', flexShrink: 0 }} />
                  {sidebarOpen && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        color: childActive ? '#2c5f2d' : '#333',
                        fontSize: '0.8125rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '& .MuiListItemText-primary': { fontSize: 'inherit' },
                      }}
                    />
                  )}
                  {sidebarOpen && (open ? <ExpandLess sx={{ color: '#666' }} /> : <ExpandMore sx={{ color: '#666' }} />)}
                </ListItemButton>
              </ListItem>
            );
            return (
              <Box key={item.text} sx={{ mb: 0.5 }}>
                {sidebarOpen ? parentButton : (
                  <Tooltip title={item.text} placement="right" arrow>
                    {parentButton}
                  </Tooltip>
                )}
                <Collapse in={sidebarOpen && open} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const isActive = isPathActive(child.path);
                      return (
                        <ListItem key={child.path} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            component={Link}
                            to={child.path}
                            sx={{
                              borderRadius: '8px',
                              minHeight: 40,
                              pl: 3.5,
                              pr: 2,
                              backgroundColor: isActive ? 'rgba(44, 95, 45, 0.15)' : 'transparent',
                              borderLeft: isActive ? '4px solid #2c5f2d' : 'none',
                              '&:hover': { backgroundColor: 'rgba(44, 95, 45, 0.1)' },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <ChildIcon sx={{ mr: 1.5, color: isActive ? '#2c5f2d' : '#666', flexShrink: 0, fontSize: 20 }} />
                            <ListItemText
                              primary={child.text}
                              sx={{
                                color: isActive ? '#2c5f2d' : '#333',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                '& .MuiListItemText-primary': { fontSize: 'inherit' },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          const Icon = item.icon;
          const isActive = isPathActive(item.path);
          const button = (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  borderRadius: '8px',
                  justifyContent: sidebarOpen ? 'initial' : 'center',
                  minHeight: 48,
                  px: sidebarOpen ? 2 : 1.5,
                  backgroundColor: isActive ? 'rgba(44, 95, 45, 0.15)' : 'transparent',
                  borderLeft: isActive ? '4px solid #2c5f2d' : 'none',
                  '&:hover': { backgroundColor: 'rgba(44, 95, 45, 0.1)' },
                  transition: 'all 0.3s ease',
                }}
              >
                <Icon sx={{ mr: sidebarOpen ? 2 : 0, color: isActive ? '#2c5f2d' : '#666', flexShrink: 0 }} />
                {sidebarOpen && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      color: isActive ? '#2c5f2d' : '#333',
                      fontSize: '0.8125rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      '& .MuiListItemText-primary': { fontSize: 'inherit' },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
          return sidebarOpen ? button : (
            <Tooltip key={item.text} title={item.text} placement="right" arrow>
              {button}
            </Tooltip>
          );
        })}
        {/* Logout Button */}
        <ListItem disablePadding sx={{ mt: 'auto', borderTop: '1px solid #eee', pt: 2 }}>
          {sidebarOpen ? (
            <ListItemButton
              onClick={handleLogoutClick}
              sx={{
                borderRadius: '8px',
                '&:hover': { backgroundColor: 'rgba(200, 0, 0, 0.1)' },
              }}
            >
              <Logout sx={{ mr: 2, color: '#c00' }} />
              <ListItemText
                primary="Logout"
                sx={{
                  color: '#c00',
                  fontSize: '0.8125rem',
                  '& .MuiListItemText-primary': { fontSize: 'inherit' },
                }}
              />
            </ListItemButton>
          ) : (
            <Tooltip title="Logout" placement="right" arrow>
              <ListItemButton
                onClick={handleLogoutClick}
                sx={{
                  borderRadius: '8px',
                  justifyContent: 'center',
                  minHeight: 48,
                  px: 1.5,
                  '&:hover': { backgroundColor: 'rgba(200, 0, 0, 0.1)' },
                }}
              >
                <Logout sx={{ color: '#c00' }} />
              </ListItemButton>
            </Tooltip>
          )}
        </ListItem>
      </List>
      {/* Open/Close Toggle */}
      <Box sx={{ p: 1, borderTop: '1px solid #eee', display: 'flex', justifyContent: sidebarOpen ? 'flex-end' : 'center' }}>
        <Tooltip title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'} placement="right" arrow>
          <IconButton
            onClick={toggleSidebar}
            sx={{
              color: '#666',
              '&:hover': { backgroundColor: 'rgba(44, 95, 45, 0.1)', color: '#2c5f2d' },
            }}
          >
            {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Tooltip>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AdminHeader onLogout={handleLogout} />
      
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: sidebarOpen ? drawerWidth : drawerWidthCollapsed,
          flexShrink: 0,
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          '& .MuiDrawer-paper': {
            width: sidebarOpen ? drawerWidth : drawerWidthCollapsed,
            boxSizing: 'border-box',
            backgroundColor: '#ffffff',
            marginTop: '64px',
            height: 'calc(100vh - 64px)',
            borderRight: '1px solid #e8ecf1',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#f5f7fa', paddingTop: '64px' }}>
        <Box sx={{ flex: 1 }}>
          <Routes>
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/my-account" element={<ProtectedRoute isAuthenticated={isAuthenticated}><MyAccountPage /></ProtectedRoute>} />
            <Route path="/admin/dashboard" element={<ProtectedRoute isAuthenticated={isAuthenticated}><DashboardPage /></ProtectedRoute>} />
            <Route path="/admin/home-sliders" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('home_slider') || hasModuleAccess('gallery') || isSuperAdmin}><HomeSlidersPage /></PermissionRoute>} />
            <Route path="/admin/home-popups" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('home_popup') || hasModuleAccess('home_slider') || isSuperAdmin}><HomePopupsPage /></PermissionRoute>} />
            <Route path="/admin/site-stats" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('site_stats') || hasModuleAccess('home_slider') || isSuperAdmin}><SiteStatsPage /></PermissionRoute>} />
            <Route path="/admin/testimonials" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('testimonials') || hasModuleAccess('home_slider') || isSuperAdmin}><TestimonialsPage /></PermissionRoute>} />
            <Route path="/admin/newsletters" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('newsletters') || hasModuleAccess('contacts') || isSuperAdmin}><NewslettersPage /></PermissionRoute>} />
            <Route path="/admin/legal-pages" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('legal_pages') || hasModuleAccess('home_slider') || isSuperAdmin}><LegalPagesPage /></PermissionRoute>} />
            <Route path="/admin/financial-documents" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('financial_documents') || hasModuleAccess('legal_pages') || isSuperAdmin}><FinancialDocumentsPage /></PermissionRoute>} />
            <Route path="/admin/founders" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('founders') || hasModuleAccess('team') || isSuperAdmin}><FoundersPage /></PermissionRoute>} />
            <Route path="/admin/team-members" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('team_members') || hasModuleAccess('team') || isSuperAdmin}><TeamPage /></PermissionRoute>} />
            <Route path="/admin/team" element={<Navigate to="/admin/team-members" replace />} />
            <Route path="/admin/team/:id/edit" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('founders') || hasModuleAccess('team_members') || hasModuleAccess('team') || isSuperAdmin}><TeamMemberEditPage /></PermissionRoute>} />
            <Route path="/admin/campaign-categories" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('campaign_categories') || hasModuleAccess('campaigns') || isSuperAdmin}><CampaignCategoriesPage /></PermissionRoute>} />
            <Route path="/admin/campaign-submissions" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('campaign_submissions') || hasModuleAccess('campaigns')}><CampaignSubmissionsPage /></PermissionRoute>} />
            <Route path="/admin/campaign-submissions/:id/review" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('campaign_submissions') || hasModuleAccess('campaigns')}><CampaignSubmissionReviewPage /></PermissionRoute>} />
            <Route path="/admin/campaign-ratings" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('ratings') || hasModuleAccess('campaigns')}><CampaignRatingsPage /></PermissionRoute>} />
            <Route path="/admin/campaigns" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('campaigns')}><CampaignsPage /></PermissionRoute>} />
            <Route path="/admin/campaigns/add" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('campaigns')}><CampaignAddPage /></PermissionRoute>} />
            <Route path="/admin/campaigns/:id/edit" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('campaigns')}><CampaignEditPage /></PermissionRoute>} />
            <Route path="/admin/campaigns/:id" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('campaigns')}><CampaignDetailPage /></PermissionRoute>} />
            <Route path="/admin/donations" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('donations')}><DonationsPage /></PermissionRoute>} />
            <Route path="/admin/donations/:id" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('donations')}><DonationDetailPage /></PermissionRoute>} />
            <Route path="/admin/volunteers" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('volunteers')}><VolunteersPage /></PermissionRoute>} />
            <Route path="/admin/users" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('users')}><UsersPage /></PermissionRoute>} />
            <Route path="/admin/gallery" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('gallery')}><AdminGalleryPage /></PermissionRoute>} />
            <Route path="/admin/gallery/:id" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('gallery')}><GalleryDetailPage /></PermissionRoute>} />
            <Route path="/admin/gallery/add" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('gallery')}><GalleryAddPage /></PermissionRoute>} />
            <Route path="/admin/gallery/:id/edit" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('gallery')}><GalleryEditPage /></PermissionRoute>} />
            <Route path="/admin/contacts" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('contacts')}><ContactsPage /></PermissionRoute>} />
            <Route path="/admin/reports" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('reports')}><ReportsPage /></PermissionRoute>} />
            <Route path="/admin/reports/:id" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={hasModuleAccess('reports')}><ReportDetailPage /></PermissionRoute>} />
            <Route path="/admin/sub-admins" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={isSuperAdmin}><SubAdminPage /></PermissionRoute>} />
            <Route path="/admin/sub-admins/add" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={isSuperAdmin}><SubAdminFormPage /></PermissionRoute>} />
            <Route path="/admin/sub-admins/edit/:id" element={<PermissionRoute isAuthenticated={isAuthenticated} canAccess={isSuperAdmin}><SubAdminFormPage /></PermissionRoute>} />
            <Route path="*" element={<Navigate to={isAuthenticated ? '/admin/dashboard' : '/admin/login'} />} />
          </Routes>
        </Box>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={openLogoutDialog}
        onClose={handleCancelLogout}
        PaperProps={{
          sx: {
            borderRadius: '8px',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            color: '#1a3a2a',
            paddingBottom: 1,
          }}
        >
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: '#666',
              marginTop: 1,
            }}
          >
            Are you sure you want to logout? You will need to login again to access the admin dashboard.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCancelLogout}
            sx={{
              color: '#666',
              textTransform: 'none',
              fontSize: '0.95rem',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              color: 'white',
              textTransform: 'none',
              fontSize: '0.95rem',
              '&:hover': {
                backgroundColor: '#b71c1c',
              },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default App;
